import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  ButtonResponsiveWrapper,
  ErrorModal,
  FlexControlledForm,
  Preloader,
  useResponsive,
} from '@common-fe/common-fe';
import * as yup from 'yup';

import AppButton from '@/components/controls/AppButton';
import SaveAsDefaultCheckbox from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/SaveAsDefaultCheckbox';
import useAddPaymentMethodQuery from '@/modules/transaction/components/ReimburseMe/queries/useAddPaymentMethod.query';
import { useUpdatePaymentMethodQuery } from '@/modules/transaction/components/ReimburseMe/queries/useUpdatePaymentMethod.query';
import { PaymentMethodOwnerType } from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { PaymentMethodType } from '@/modules/user/user.types';

import { useVenmoFields, VenmoInfo } from './hooks/useVemoFields';

const BUTTON_WIDTH = '150px';

interface Props {
  onBack?: () => void;
  onSuccess?: () => void;
  editableMethodId?: string;
  allowSaveAsDefault?: boolean;
  onError?: () => void;
}

export const AddVenmoForm: React.FC<Props> = ({
  onBack,
  onSuccess,
  editableMethodId,
  allowSaveAsDefault,
  onError,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const [isSaveAsDefault, setIsSaveAsDefault] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { save, isLoading, isSuccess } = useAddPaymentMethodQuery(onSuccess);
  const {
    updatePaymentMethod,
    isLoading: isUpdateLoading,
    isSuccess: isUpdateSuccess,
  } = useUpdatePaymentMethodQuery(onSuccess);
  const fields = useVenmoFields();
  const [formValues, setFormValues] = useState<VenmoInfo>({});
  const [activeValues, setActiveValues] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChangeValues = useCallback(
    (values: VenmoInfo) => {
      setFormValues((prev) => ({ ...prev, ...values }));
      try {
        const validatorMap = fields.reduce(
          (map, field) => ({
            ...map,
            [field.name]: field.validator,
          }),
          {}
        );
        const schema = yup.object().shape(validatorMap);
        schema.validateSync(values, { abortEarly: false });
        setActiveValues(true);
      } catch {
        setActiveValues(false);
      }
    },
    [formValues.phoneNumber]
  );

  const handleSubmit = useCallback(async () => {
    try {
      setSubmitted(true);
      if (activeValues && onSuccess) {
        if (editableMethodId) {
          await updatePaymentMethod({
            paymentMethodId: editableMethodId,
            paymentType: PaymentMethodType.VENMO,
            venmoData: formValues,
          });
        } else {
          await save({
            paymentType: PaymentMethodType.VENMO,
            paymentOwnerType: PaymentMethodOwnerType.EMPLOYEE,
            venmoData: formValues,
            isDefault: isSaveAsDefault,
          });
        }
      }
    } catch (e) {
      // @ts-ignore
      if (onError && e.response.status === 409) {
        return onError();
      }
      // @ts-ignore
      setErrorMessage(e?.response?.data?.elevate_error_message);
    }
  }, [
    onError,
    activeValues,
    onSuccess,
    editableMethodId,
    updatePaymentMethod,
    formValues,
    save,
    isSaveAsDefault,
  ]);

  useEffect(() => {
    if (onSuccess && (isUpdateSuccess || isSuccess)) onSuccess();
  }, [isSuccess, isUpdateSuccess, onSuccess]);

  return (
    <>
      <Box pad={isMobile ? 'spacing12' : 'spacing24'} round="moduleRound" background="module">
        <Box round="container1Round" background="canvas">
          <FlexControlledForm
            fields={fields}
            showError={submitted}
            editMode
            onChangeValues={handleChangeValues}
          />
        </Box>
      </Box>
      <Box pad={{ top: 'medium' }} direction={isMobile ? 'column' : 'row'} align="center">
        {allowSaveAsDefault && (
          <Box fill={isMobile}>
            <SaveAsDefaultCheckbox
              isSaveAsDefault={isSaveAsDefault}
              setIsSaveAsDefault={setIsSaveAsDefault}
            />
          </Box>
        )}
        <ButtonResponsiveWrapper
          fill={isMobile ? true : undefined}
          align="center"
          margin={isMobile ? {} : { left: 'auto' }}
        >
          <AppButton
            testId="Venmo_form_back"
            buttonType="secondary"
            type="button"
            onClick={onBack}
            width={isMobile ? '100%' : BUTTON_WIDTH}
          >
            {editableMethodId ? t('Cancel') : t('Back')}
          </AppButton>
          <AppButton type="button" width={isMobile ? '100%' : BUTTON_WIDTH} onClick={handleSubmit}>
            {isLoading || isUpdateLoading ? <Preloader color="white" /> : t('Submit')}
          </AppButton>
        </ButtonResponsiveWrapper>
      </Box>

      {errorMessage ? (
        <ErrorModal
          testId="AddVenmoForm-error"
          visible
          header="Error"
          helpText={errorMessage}
          buttonText="Close"
          onSetVisible={() => {
            setErrorMessage('');
          }}
        />
      ) : null}
    </>
  );
};
